import React from "react";
import ReactQuill from "react-quill-new";
import type { EmitterSource } from "react-quill-new";
import type Delta from "quill-delta";
import "react-quill-new/dist/quill.snow.css";

import { addRichText, saveWidget } from "../../actions/layoutActions";
import { hideModal } from "../../actions/modalActions";
import { useRequiredValidation } from "../../custom-hooks/useRequiredValidation";
import {
  eventCategories,
  recordEvent
} from "../../utils/googleAnalyticsEvents";
import Button from "../button/button";
import "./RichText.scss";
import { useAppSelector, useAppDispatch } from "../../hooks";
import type { ButtonActivationEvent } from "../button/button";

export interface EditRichTextProps {
  widget_id: string;
}

/** The restricted editor handle react-quill hands to onChange. */
type ReactQuillEditor = Parameters<
  NonNullable<React.ComponentProps<typeof ReactQuill>["onChange"]>
>[3];

export default function EditRichText({ widget_id }: EditRichTextProps) {
  const dispatch = useAppDispatch();
  const richTexts = useAppSelector(state => state.layouts.richTexts);

  const richText = (richTexts
    ? richTexts.find(rt => rt.widget_id === widget_id)
    : null) ?? { delta: null };
  const [delta, setDelta] = React.useState(richText.delta);

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" }
      ],
      ["link"],
      ["clean"]
    ]
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "link",
    "image"
  ];

  function handleChange(
    _content: string,
    _delta: Delta,
    _source: EmitterSource,
    editor: ReactQuillEditor
  ) {
    setDelta(editor.getContents());
  }

  const deltaRequired = useRequiredValidation(true, delta);

  const onSubmit = (event: ButtonActivationEvent) => {
    event.preventDefault();
    deltaRequired.onInvalid();
    if (deltaRequired.isFieldFilled) {
      recordEvent(
        "Custom Text Config Saved",
        eventCategories.COMPONENT_CONFIGURATION,
        "Custom Text Config Saved"
      );
      dispatch(saveWidget(widget_id));
      dispatch(
        addRichText({
          widget_id: widget_id,
          delta
        })
      );
      dispatch(hideModal());
    }
  };

  return (
    <div className="rich-text-editor-container">
      <ReactQuill
        theme="snow"
        value={delta ?? ""}
        onChange={handleChange}
        modules={modules}
        formats={formats}
      />
      <Button
        type="submit"
        onClick={onSubmit}
        usePermanentAccent
        className="rich-text-button"
      >
        Save Custom Text
      </Button>
    </div>
  );
}
